<template>
  <div class="project-detail-page">
    <!-- Header with back button, logo and language switcher -->
    <header class="project-header">
      <div class="container">
        <button @click="goBack" class="back-btn">
          <i class="fas fa-arrow-left"></i>
          {{ locale === 'tr' ? 'Geri Dön' : 'Go Back' }}
        </button>
        
        <div class="header-logo" @click="goToHome">
          <img src="/images/aslan-energy-logo-hq.jpg" alt="Aslan Energy" class="header-logo-img" />
          <div class="header-logo-text">
            <span class="company-name">Aslan Energy</span>
          </div>
        </div>
        
        <div class="language-switcher">
          <button 
            @click="changeLanguage('tr')" 
            :class="{ active: locale === 'tr' }"
            class="lang-btn"
          >
            TR
          </button>
          <button 
            @click="changeLanguage('en')" 
            :class="{ active: locale === 'en' }"
            class="lang-btn"
          >
            EN
          </button>
        </div>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ locale === 'tr' ? 'Proje detayları yükleniyor...' : 'Loading project details...' }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <h2>{{ locale === 'tr' ? 'Proje Bulunamadı' : 'Project Not Found' }}</h2>
      <p>{{ error }}</p>
      <button @click="goBack" class="error-back-btn">
        {{ locale === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home' }}
      </button>
    </div>

    <!-- Project Detail Content -->
    <main v-else-if="project" class="project-detail-content">
      <!-- Hero Section -->
      <section class="project-hero">
        <div class="project-hero-image">
          <img 
            :src="project.image_url || 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'" 
            :alt="projectTitle"
            class="hero-img"
          />
          <div class="hero-overlay">
            <div class="hero-content">
              <h1 class="project-title">{{ projectTitle }}</h1>
              <div class="project-meta-hero">
                <span class="capacity" v-if="project.capacity">⚡ {{ project.capacity }}</span>
                <span class="location" v-if="projectLocation">📍 {{ projectLocation }}</span>
                <span class="date" v-if="project.completion_date">📅 {{ formatDate(project.completion_date) }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Project Info Grid -->
      <section class="project-info-section">
        <div class="container">
          <div class="project-info-grid">
            <!-- Description Card -->
            <div class="info-card description-card">
              <div class="card-header">
                <div class="card-icon">📋</div>
                <h3>{{ locale === 'tr' ? 'Proje Açıklaması' : 'Project Description' }}</h3>
              </div>
              <div class="card-content">
                <p>{{ projectDescription }}</p>
              </div>
            </div>

            <!-- Technical Specs Card -->
            <div class="info-card specs-card">
              <div class="card-header">
                <div class="card-icon">⚙️</div>
                <h3>{{ locale === 'tr' ? 'Teknik Özellikler' : 'Technical Specifications' }}</h3>
              </div>
              <div class="card-content">
                <div class="spec-item" v-if="project.capacity">
                  <span class="spec-label">{{ locale === 'tr' ? 'Kurulu Güç:' : 'Installed Power:' }}</span>
                  <span class="spec-value">{{ project.capacity }}</span>
                </div>
                <div class="spec-item" v-if="project.panel_count">
                  <span class="spec-label">{{ locale === 'tr' ? 'Panel Sayısı:' : 'Panel Count:' }}</span>
                  <span class="spec-value">{{ project.panel_count }}</span>
                </div>
                <div class="spec-item" v-if="project.inverter_type">
                  <span class="spec-label">{{ locale === 'tr' ? 'İnvertör Tipi:' : 'Inverter Type:' }}</span>
                  <span class="spec-value">{{ project.inverter_type }}</span>
                </div>
                <div class="spec-item" v-if="project.annual_production">
                  <span class="spec-label">{{ locale === 'tr' ? 'Yıllık Üretim:' : 'Annual Production:' }}</span>
                  <span class="spec-value">{{ project.annual_production }} kWh</span>
                </div>
              </div>
            </div>

            <!-- Client Info Card -->
            <div class="info-card client-card" v-if="project.client_name">
              <div class="card-header">
                <div class="card-icon">🏢</div>
                <h3>{{ locale === 'tr' ? 'Müşteri Bilgileri' : 'Client Information' }}</h3>
              </div>
              <div class="card-content">
                <div class="client-info">
                  <h4>{{ project.client_name }}</h4>
                  <p v-if="project.client_sector">{{ project.client_sector }}</p>
                </div>
              </div>
            </div>

            <!-- Project Timeline Card -->
            <div class="info-card timeline-card">
              <div class="card-header">
                <div class="card-icon">📅</div>
                <h3>{{ locale === 'tr' ? 'Proje Zaman Çizelgesi' : 'Project Timeline' }}</h3>
              </div>
              <div class="card-content">
                <div class="timeline-item" v-if="project.start_date">
                  <span class="timeline-label">{{ locale === 'tr' ? 'Başlangıç:' : 'Start Date:' }}</span>
                  <span class="timeline-date">{{ formatDate(project.start_date) }}</span>
                </div>
                <div class="timeline-item" v-if="project.completion_date">
                  <span class="timeline-label">{{ locale === 'tr' ? 'Tamamlanma:' : 'Completion:' }}</span>
                  <span class="timeline-date">{{ formatDate(project.completion_date) }}</span>
                </div>
                <div class="timeline-item" v-if="project.duration">
                  <span class="timeline-label">{{ locale === 'tr' ? 'Süre:' : 'Duration:' }}</span>
                  <span class="timeline-date">{{ project.duration }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact CTA -->
      <section class="project-contact-cta">
        <div class="container">
          <div class="cta-content">
            <h2>{{ locale === 'tr' ? 'Benzer Bir Proje İstiyorum' : 'I Want a Similar Project' }}</h2>
            <p>{{ locale === 'tr' ? 'Size özel bir teklif hazırlayalım' : 'Let us prepare a custom offer for you' }}</p>
            <button @click="goToContact" class="cta-btn">
              {{ locale === 'tr' ? 'İletişime Geçin' : 'Get in Touch' }}
            </button>
          </div>
        </div>
      </section>
    </main>

    <!-- Modern Footer -->
    <footer class="modern-footer">
      <div class="footer-container">
        <div class="footer-content">
          <div class="footer-section company-info">
            <div class="footer-logo" @click="goToHome">
                                          <img src="/images/aslan-energy-logo-hq.jpg" alt="Aslan Energy" class="footer-logo-img" />
              <div class="footer-logo-text">
                <span class="company-name">Aslan Energy</span>
              </div>
            </div>
            <p class="company-description">
              {{ locale === 'tr' 
                ? 'Aslan Energy olarak yenilenebilir enerji çözümleri ile sürdürülebilir geleceği inşa ediyoruz.' 
                : 'As Aslan Energy, we are building a sustainable future with renewable energy solutions.' 
              }}
            </p>
            <div class="social-links">
              <a href="https://www.instagram.com/aslanenergy/" class="social-link" target="_blank"><i class="fab fa-instagram"></i></a>
              <a href="https://www.facebook.com/p/Aslan-Energy-100054519006747/" class="social-link" target="_blank"><i class="fab fa-facebook"></i></a>
              <a href="https://www.linkedin.com/company/aslan-energy-madenci%CC%87li%CC%87k-san-ve-ti%CC%87c-ltd-%C5%9Fti%CC%87" class="social-link" target="_blank"><i class="fab fa-linkedin"></i></a>
              <a href="https://www.youtube.com/@aslanenergy" class="social-link" target="_blank"><i class="fab fa-youtube"></i></a>
            </div>
          </div>

          <div class="footer-section quick-links">
            <h4>{{ locale === 'tr' ? 'Hızlı Linkler' : 'Quick Links' }}</h4>
            <ul>
              <li><a href="/">{{ locale === 'tr' ? 'Ana Sayfa' : 'Home' }}</a></li>
              <li><a href="/?page=projects">{{ locale === 'tr' ? 'Projelerimiz' : 'Our Projects' }}</a></li>
              <li><a href="/#services">{{ locale === 'tr' ? 'Hizmetlerimiz' : 'Our Services' }}</a></li>
              <li><a href="/#about">{{ locale === 'tr' ? 'Hakkımızda' : 'About Us' }}</a></li>
            </ul>
          </div>
        </div>

        <div class="footer-bottom">
          <div class="footer-bottom-content">
            <p>&copy; 2025 Aslan Energy. {{ locale === 'tr' ? 'Tüm hakları saklıdır.' : 'All rights reserved.' }}</p>
            <div class="footer-links">
              <a href="#">{{ locale === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy' }}</a>
              <a href="#">{{ locale === 'tr' ? 'Kullanım Şartları' : 'Terms of Service' }}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import ApiService from '../services/api.js'

// Data
const locale = ref('tr')
const loading = ref(true)
const error = ref()
const project = ref(null)
const projectId = ref(null)

// Computed properties
const projectTitle = computed(() => {
  if (!project.value) return ''
  return locale.value === 'tr' ? project.value.title_tr : project.value.title_en
})

const projectDescription = computed(() => {
  if (!project.value) return ''
  return locale.value === 'tr' ? project.value.description_tr : project.value.description_en
})

const projectLocation = computed(() => {
  if (!project.value) return ''
  return locale.value === 'tr' ? project.value.location_tr : project.value.location_en
})

// Methods
const changeLanguage = (lang) => {
  locale.value = lang
  localStorage.setItem('preferred-language', lang)
  // Sayfayı yeniden yükle ki değişiklikler görünür olsun
  setTimeout(() => {
    window.location.reload()
  }, 100)
}

const goBack = () => {
  window.location.href = window.location.pathname
}

const goToContact = () => {
  window.location.href = '/#contact'
}

const goToHome = () => {
  window.location.href = '/'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString(locale.value === 'tr' ? 'tr-TR' : 'en-US')
}

const getProjectIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('id')
}

const loadProject = async (id) => {
  try {
    loading.value = true
    error.value = null
    
    // API'den proje detaylarını çek
    const projectData = await ApiService.getProjectById(id)
    
    if (projectData) {
      project.value = projectData
    } else {
      error.value = locale.value === 'tr' 
        ? 'Proje bulunamadı veya erişilemiyor.' 
        : 'Project not found or inaccessible.'
    }
  } catch (err) {
    // console.error('Proje yüklenirken hata:', err)
    error.value = locale.value === 'tr' 
      ? 'Proje yüklenirken bir hata oluştu.' 
      : 'An error occurred while loading the project.'
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Home header'ını gizle
  document.body.classList.add('project-detail-page-active')
  
  // Dil tercihini localStorage'dan al
  const savedLang = localStorage.getItem('preferred-language')
  if (savedLang) {
    locale.value = savedLang
  }
  
  // URL'den proje ID'sini al
  projectId.value = getProjectIdFromUrl()
  
  if (projectId.value) {
    loadProject(projectId.value)
  } else {
    error.value = locale.value === 'tr' 
      ? 'Geçersiz proje ID\'si.' 
      : 'Invalid project ID.'
    loading.value = false
  }
})

// Cleanup
onUnmounted(() => {
  // Home header'ını tekrar göster
  document.body.classList.remove('project-detail-page-active')
})
</script>

<style scoped>
.project-detail-page {
  min-height: 100vh;
  background: var(--gradient-secondary);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  overflow-y: auto;
}

/* Hide home page header when project detail is active */
:global(body.project-detail-page-active .custom-header),
:global(body.project-detail-page-active .sticky-header),
:global(body.project-detail-page-active .nav-container),
:global(body.project-detail-page-active .navbar),
:global(body.project-detail-page-active .main-header),
:global(body.project-detail-page-active header:not(.project-header)),
:global(body.project-detail-page-active .header),
:global(body.project-detail-page-active nav) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  top: -9999px !important;
}

/* Header */
.project-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}



.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #388e3c;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: var(--secondary-dark);
}

.language-switcher {
  display: flex;
  gap: 0.5rem;
}

.lang-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #388e3c;
  background: transparent;
  color: #388e3c;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
}

.lang-btn.active,
.lang-btn:hover {
  background: #388e3c;
  color: white;
}

/* Header Logo */
.header-logo {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(56, 142, 60, 0.1);
  border: 2px solid rgba(56, 142, 60, 0.2);
  border-radius: 12px;
  padding: 0.5rem 1rem;
  backdrop-filter: blur(5px);
}

.header-logo:hover {
  background: rgba(56, 142, 60, 0.15);
  border-color: rgba(56, 142, 60, 0.3);
  transform: translateX(-50%) translateY(-1px);
  box-shadow: 0 4px 15px rgba(56, 142, 60, 0.2);
}

.header-logo img {
  height: 32px;
  width: auto;
}

.header-logo-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #388e3c;
  letter-spacing: 0.02em;
}

/* Loading & Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  padding: 2rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #388e3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-back-btn {
  padding: 1rem 2rem;
  background: #388e3c;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1rem;
  transition: background 0.3s ease;
}

.error-back-btn:hover {
  background: var(--primary-gold-light);
}

/* Hero Section */
.project-hero {
  position: relative;
  height: 60vh;
  min-height: 500px;
  overflow: hidden;
}

.project-hero-image {
  position: relative;
  width: 100%;
  height: 100%;
}

.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(rgba(56, 142, 60, 0.7), rgba(0, 0, 0, 0.5));
  display: flex;
  align-items: flex-end;
  padding: 2rem;
}

.hero-content {
  color: white;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.project-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.project-meta-hero {
  display: flex;
  gap: 2.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.project-meta-hero span {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 1rem 1.5rem;
  border-radius: 25px;
  font-size: 1.4rem;
  font-weight: 600;
}

/* Info Section */
.project-info-section {
  padding: 4rem 2rem;
}

.project-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.info-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  min-height: 280px;
}

.info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.card-icon {
  font-size: 2rem;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #388e3c, #4caf50);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-header h3 {
  color: #333;
  font-size: 1.8rem;
  font-weight: 700;
}

.card-content {
  color: #666;
  line-height: 1.8;
  font-size: 1.1rem;
}

.spec-item,
.timeline-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid #eee;
}

.spec-item:last-child,
.timeline-item:last-child {
  border-bottom: none;
}

.spec-label,
.timeline-label {
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.spec-value,
.timeline-date {
  color: #388e3c;
  font-weight: 600;
  font-size: 1.2rem;
}

.client-info h4 {
  color: #333;
  margin-bottom: 0.5rem;
}

/* Contact CTA */
.project-contact-cta {
  background: linear-gradient(135deg, #388e3c, #4caf50);
  padding: 4rem 0;
  text-align: center;
}

.cta-content {
  color: white;
}

.cta-content h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.cta-content p {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.cta-btn {
  background: var(--primary-gold);
  color: #333;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cta-btn:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

/* Modern Footer */
.modern-footer {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  padding: 4rem 0 0;
  margin-top: 2rem;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 4rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background: white;
  border: 2px solid var(--primary-gold);
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: fit-content;
}

.footer-logo:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(201, 160, 64, 0.3);
  border-color: var(--primary-gold-dark);
}

.footer-logo-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: contain;
}

.footer-logo-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.footer-logo-text .company-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-gold);
  line-height: 1;
}

.footer-section h4 {
  color: var(--primary-gold);
  margin-bottom: 1.5rem;
  font-size: 1.4rem;
  font-weight: 700;
}

.company-description {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.2rem;
}

.social-link:hover {
  background: var(--primary-gold);
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(201, 160, 64, 0.4);
}

.quick-links ul {
  list-style: none;
  padding: 0;
}

.quick-links li {
  margin-bottom: 0.8rem;
}

.quick-links a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 1rem;
}

.quick-links a:hover {
  color: var(--primary-gold);
}



.footer-bottom {
  padding: 2rem 0;
  background: rgba(0, 0, 0, 0.2);
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.7);
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: var(--primary-gold);
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  .footer-bottom-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .footer-links {
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .project-header {
    padding: 0.6rem 0;
  }
  
  .project-header .container {
    padding: 0 1rem;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }
  
  .header-logo {
    position: relative;
    left: auto;
    transform: none;
    order: 0;
    padding: 0.3rem 0.6rem;
  }
  
  .header-logo img {
    height: 24px;
  }
  
  .header-logo-text {
    font-size: 0.8rem;
  }
  
  .back-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
  
  .lang-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    min-width: 35px;
  }
  
  .language-switcher {
    gap: 0.3rem;
  }
  
  .project-title {
    font-size: 2rem;
  }
  
  .project-meta-hero {
    gap: 1.5rem;
  }
  
  .project-meta-hero span {
    font-size: 1.1rem;
    padding: 0.8rem 1.2rem;
  }
  
  .hero-overlay {
    padding: 1rem;
  }
  
  .project-info-section {
    padding: 3rem 1rem;
  }
  
  .project-info-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .info-card {
    padding: 2rem;
    min-height: auto;
  }
  
  .spec-item,
  .timeline-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .cta-content h2 {
    font-size: 2rem;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .project-hero {
    height: 50vh;
    min-height: 400px;
  }
  
  .project-title {
    font-size: 1.5rem;
  }
  
  .project-header {
    padding: 0.4rem 0;
  }
  
  .project-header .container {
    padding: 0 0.5rem;
    gap: 0.3rem;
  }
  
  .header-logo {
    padding: 0.2rem 0.4rem;
  }
  
  .header-logo img {
    height: 20px;
  }
  
  .header-logo-text {
    font-size: 0.7rem;
  }
  
  .back-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }
  
  .back-btn i {
    font-size: 0.7rem;
  }
  
  .lang-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    min-width: 30px;
  }
  
  .cta-content h2 {
    font-size: 1.8rem;
  }
  
  .cta-content p {
    font-size: 1rem;
  }
}
</style>